// completion-repair.js — re-derive a completion the write lost, on lesson entry.
//
// Brief 199, criterion 5. iOS does not trust a single terminal write either: it
// repairs on re-entry. `LessonChartViewController.m:200 updateFolderCompletedState`
// runs on EVERY entry to the lesson chart (:147 when it appears, :1738 once the
// plists have loaded) and, when the lesson's `completed` flag is 0 while its
// `items_completed_number` says the work is done, writes `completed = 1`. The
// learner does nothing and sees nothing.
//
// The web already has the lesson/stage half of that: `pathway-builder` derives
// a lesson's state from its items' `completedCount`, so it is never a stale
// flag. What it had no answer for is the ITEM row itself — the nine items of
// the 2022 walk whose drills ran to the end while the completion write was lost
// (docs/193-handover.md §§1-2 of "Part B — round 3"). This module is that
// answer, scoped to evidence the web already stores and can trust:
//
//   • L2B Training (type 9) — every one of the item's L2B-eligible MMUs is
//     mastered in `l2bStreaks` FOR THIS ITEM (Brief 182's per-(item, MMU)
//     scope). This is not a new inference: `kfe-l2b-exercise.onConnected`
//     already treats an empty post-mastery pool as "complete" and marks the
//     item completed on re-entry. The repair just stops requiring the learner
//     to re-open the drill to get it, which is what iOS's chart-entry repair
//     does. A partial drill cannot produce it — the engine's completion IS all
//     MMUs reaching the mastery streak.
//   • L2B Testing (type 10) — the row still carries the drill's `testState`
//     with a result for EVERY authored round (`results.length >=
//     numberOfInstances`), which is the shape mechanism 1 left behind. A
//     partial test cannot produce it: it has a result per round played, and a
//     played-every-round test is a finished test (the engine completes on the
//     last round's feedback, with no further learner input). Where the met pool
//     was shorter than `numberOfInstances` the test is shorter too, so this
//     under-repairs rather than over-repairs, deliberately.
//
// What it deliberately does NOT repair, because nothing stored can tell a
// finished attempt from an abandoned one:
//
//   • multiple choice, true/false, HAAI, HTML, music track, audio,
//     self-recording. For MC and T/F the completion write is the ONLY thing the
//     run would have stored (the three MC items of the walk left no trace at
//     all), so there is no evidence to re-derive from — inventing one would
//     mean counting an abandoned attempt as a pass. Mechanism 2's retry is what
//     protects those; a failure that outlives the retry is surfaced to the
//     learner instead (criterion 4) and the learner plays it again.
//   • Self-Recording, whose `srProgress` rows DO record per-word progress, but
//     whose per-word `completed` flags are written as the learner goes, so
//     "every word done" and "the session ended on the last word" are the same
//     stored state only if the final write landed — the same write this brief
//     is about. Left alone.
//
// Silent by design (Rule #9): iOS repairs without telling the learner, so this
// logs and repairs, and the only learner-visible message in this brief is for
// the UNREPAIRABLE case (criterion 4).

import {
  getItemProgress,
  markItemCompleted,
  getMasteredL2bMmuIdsForItem,
  updateItemProgress,
} from "./learner-store.js";
import {
  getItem,
  ITEM_TYPE_L2B_TRAINING,
  ITEM_TYPE_L2B_TESTING,
} from "./content-items.js";
import { getMmusForItem, loadMmuRegistry } from "./mmu-loader.js";
import { applyL2bDefensiveFilters } from "../engines/l2b-engine.js";

const DEFAULT_TEST_INSTANCES = 8; // L2BTestEngine's own default

// Pathway lesson JSON carries STRING item types; kfe_content_items.json carries
// numeric ones. The repair runs off the lesson row (which is what the lesson
// screen has in hand) and falls back to the registry, so it works either way —
// same two-shape problem `item-titles.js` solves with the same kind of map.
const TYPE_STRING_TO_NUMERIC = /** @type {Record<string, number>} */ ({
  l2b_exercise: ITEM_TYPE_L2B_TRAINING,
  l2b_test: ITEM_TYPE_L2B_TESTING,
});

/**
 * Repair any item in this lesson whose completion is contradicted by evidence
 * its exercise left behind. Called on lesson entry, as iOS calls
 * `updateFolderCompletedState`. Fully guarded: a repair that cannot be decided
 * is skipped, and nothing here throws back into the screen's load path.
 *
 * @param {Array<{id: string|number, type?: string|number}>} items the lesson's items
 * @returns {Promise<number[]>} the itemIds repaired (numeric), for logging/tests
 */
export async function repairLostCompletions(items) {
  /** @type {number[]} */ const repaired = [];
  for (const item of items ?? []) {
    const itemId = Number(String(item?.id ?? "").replace(/^item_/, ""));
    if (!Number.isFinite(itemId)) continue;
    try {
      if (await repairItemCompletion(itemId, item?.type)) repaired.push(itemId);
    } catch (err) {
      console.warn("[completion-repair] could not evaluate item", itemId, err);
    }
  }
  return repaired;
}

/**
 * Repair one item if — and only if — the stored evidence says the drill ran to
 * the end. Returns true when a completion was written.
 * @param {number} itemId
 * @param {string|number} [itemType] the lesson row's type, when the caller has it
 * @returns {Promise<boolean>}
 */
export async function repairItemCompletion(itemId, itemType = undefined) {
  const row = /** @type {any} */ (await getItemProgress(itemId));
  // Only ever repairs UPWARDS from zero. A count ≥ 1 is the learner's record
  // and is never touched (`completedCount` is a counter, not a flag — Brief 072).
  if ((row?.completedCount ?? 0) > 0) return false;

  // The lesson row's own type is enough in the live app — lesson-detail does
  // not load the content-items registry, and `getItem` THROWS when it is not
  // loaded, so it is consulted only as a fallback and never allowed to throw.
  const content = contentItemOrNull(itemId);
  const type = resolveType(itemType) ?? resolveType(content?.type);

  if (type === ITEM_TYPE_L2B_TRAINING) {
    if (!(await l2bTrainingRanToTheEnd(itemId))) return false;
    await recordRepair(itemId, "every L2B Training word is mastered for this item");
    return true;
  }

  if (type === ITEM_TYPE_L2B_TESTING) {
    if (!l2bTestRanToTheEnd(row, content)) return false;
    // Clear the dead mid-test state the lost completion left behind, exactly as
    // the completion path would have (`_persistComplete`), then record.
    await updateItemProgress(itemId, { testState: null });
    await recordRepair(itemId, "the stored test state holds a result for every round");
    return true;
  }

  return false;
}

/**
 * The content-items registry row, or null when the registry is not loaded in
 * this context (the lesson screen does not load it; the exercise screens do).
 * @param {number} itemId
 */
function contentItemOrNull(itemId) {
  try {
    return /** @type {any} */ (getItem(itemId)) ?? null;
  } catch {
    return null;
  }
}

/**
 * Numeric item type from either shape (string lesson row or numeric registry).
 * @param {string|number|undefined} raw
 * @returns {number|null}
 */
function resolveType(raw) {
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string") {
    if (TYPE_STRING_TO_NUMERIC[raw] != null) return TYPE_STRING_TO_NUMERIC[raw];
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Every L2B-eligible MMU of this item is mastered WITHIN this item. Same
 * inference `kfe-l2b-exercise.onConnected` makes when its post-mastery pool
 * comes back empty.
 * @param {number} itemId
 */
async function l2bTrainingRanToTheEnd(itemId) {
  // Cheap gate first: an item the learner never drilled has no per-(item, MMU)
  // streak rows at all, so there is nothing to re-derive from — and we avoid
  // pulling the MMU registry on a lesson open that cannot repair anything.
  const mastered = await getMasteredL2bMmuIdsForItem(itemId);
  if (mastered.length === 0) return false;
  // Only now is the registry worth loading (memoised; a no-op once loaded, and
  // the L2B screens load it anyway).
  await loadMmuRegistry();
  const mmus = applyL2bDefensiveFilters(getMmusForItem(itemId) ?? []);
  // An item with no MMUs tells us nothing.
  if (mmus.length === 0) return false;
  const masteredIds = new Set(mastered);
  return mmus.every((m) => masteredIds.has(m.mmuId));
}

/**
 * The row carries a test state with a result for every authored round.
 * @param {any} row the itemProgress row (may be undefined)
 * @param {any} content the content item
 */
function l2bTestRanToTheEnd(row, content) {
  const testState = row?.testState;
  if (!testState || typeof testState !== "object") return false;
  const results = Array.isArray(testState.results) ? testState.results : null;
  if (!results) return false;
  const authored = Number(content?.numberOfInstances);
  const rounds = Number.isFinite(authored) && authored > 0 ? authored : DEFAULT_TEST_INSTANCES;
  return results.length >= rounds;
}

/**
 * Write the repaired completion through the one completion funnel, so the
 * roll-up tick (Brief 163), the CRM row, the Global-Playlist hook, the
 * lesson-completion signal and the word encounters all fire exactly as they
 * would for a completion recorded at the end of the drill.
 * @param {number} itemId
 * @param {string} because
 */
async function recordRepair(itemId, because) {
  await markItemCompleted(itemId);
  console.info(`[completion-repair] repaired item ${itemId} — ${because}`);
}
